import React from 'react';
import { Select, TimePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';

const { Option } = Select;

const ScheduleStep: React.FC = () => {
	const { currentCampaign, updateCurrentCampaign } = useOutbound();

	const timezones = moment.tz.names().map((tz) => ({
		value: tz,
		label: `${tz} (${moment.tz(tz).format('Z')})`,
	}));

	const handleScheduleChange = (field: string, value: any) => {
		if (!currentCampaign) {
			return;
		}

		if (field === 'maxRetries') {
			updateCurrentCampaign({ maxRetries: value });
		} else {
			const updatedSchedule = {
				...currentCampaign.schedule,
				[field]: value,
			};
			updateCurrentCampaign({ schedule: updatedSchedule });
		}
	};

	const handleTimeChange = (field: string, time: any) => {
		if (time) {
			handleScheduleChange(field, time.format('HH:mm'));
		} else {
			handleScheduleChange(field, '');
		}
	};

	const schedule = currentCampaign?.schedule;

	return (
		<div className="w-full flex flex-col items-center justify-center py-6">
			<h2 className="text-xl font-semibold mb-6 text-center text-gray-800">Campaign Schedule</h2>
			<div className="w-full max-w-xl mx-auto">
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium mb-2">
							Working Hours Start <span className="text-red-500">*</span>
						</label>
						<TimePicker
							value={schedule?.startTime ? dayjs(schedule.startTime, 'HH:mm') : null}
							onChange={(time) => handleTimeChange('startTime', time)}
							format="HH:mm"
							className="w-full"
							size="large"
							placeholder="Select start time"
							needConfirm={false}
							showNow={false}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium mb-2">
							Working Hours End <span className="text-red-500">*</span>
						</label>
						<TimePicker
							value={schedule?.endTime ? dayjs(schedule.endTime, 'HH:mm') : null}
							onChange={(time) => handleTimeChange('endTime', time)}
							format="HH:mm"
							className="w-full"
							size="large"
							placeholder="Select end time"
							needConfirm={false}
							showNow={false}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium mb-2">
							Max Retry Attempts <span className="text-red-500">*</span>
						</label>
						<InputNumber
							value={currentCampaign?.maxRetries}
							onChange={(value) => handleScheduleChange('maxRetries', value)}
							className="w-full"
							size="large"
							placeholder="1-10"
							min={1}
							max={10}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium mb-2">
							Timezone <span className="text-red-500">*</span>
						</label>
						<Select
							value={schedule?.timezone}
							onChange={(value) => handleScheduleChange('timezone', value)}
							className="w-full"
							size="large"
							placeholder="Select timezone"
							showSearch
							filterOption={(input, option) => {
								const label = option?.children || option?.label || '';
								return String(label).toLowerCase().includes(input.toLowerCase());
							}}
						>
							{timezones.map((tz) => (
								<Option key={tz.value} value={tz.value} label={tz.label}>
									{tz.label}
								</Option>
							))}
						</Select>
					</div>
				</div>

				<div className="mt-4">
					<label className="block text-sm font-medium mb-2">
						Days of Week <span className="text-red-500">*</span>
					</label>
					<Select
						mode="multiple"
						value={schedule?.daysOfWeek || []}
						onChange={(value) => handleScheduleChange('daysOfWeek', value)}
						className="w-full"
						size="large"
						placeholder="Select days of the week"
					>
						<Option value={1}>Monday</Option>
						<Option value={2}>Tuesday</Option>
						<Option value={3}>Wednesday</Option>
						<Option value={4}>Thursday</Option>
						<Option value={5}>Friday</Option>
						<Option value={6}>Saturday</Option>
						<Option value={7}>Sunday</Option>
					</Select>
				</div>
			</div>
		</div>
	);
};

export default ScheduleStep;
